// errorTracker.js

/**
 * 前端错误监控工具 (添加breadcrumbs功能)
 * 功能：
 * 1. 捕获JavaScript运行时错误
 * 2. 捕获资源加载错误
 * 3. 捕获Promise未处理的rejection
 * 4. 捕获Vue错误
 * 5. 支持自定义错误上报
 * 6. 兼容IE11
 * 7. 添加breadcrumbs功能
 * 8. 新增资源健康验证，避免图片资源误报
 */
import _ from "lodash";
import MD5 from 'js-md5';



class ErrorTracker {
  constructor(options = {}) {
    this.options = {
      // 上报服务器地址
      reportUrl: options.reportUrl || "",
      // 应用ID
      appId: options.appId || "",
      // 上报间隔时间(毫秒)
      reportInterval: options.reportInterval || 5000,
      // 是否立即上报
      immediate: options.immediate !== false,
      // 最大错误数
      maxErrors: options.maxErrors || 10,
      // 最大breadcrumbs数量
      maxBreadcrumbs: options.maxBreadcrumbs || 5,
      // 自定义上报数据
      extraData: options.extraData || {},
      // 是否开启console.error输出
      consoleError: options.consoleError !== false,
      // 是否开启debug模式
      debug: options.debug || false,
    };

    // 错误队列
    this.errorQueue = [];
    // 错误id
    this.errorId = [];
    // breadcrumbs队列
    this.breadcrumbs = [];
    // 上报计时器
    this.reportTimer = null;
    // 是否已初始化
    this.initialized = false;
    // 资源健康验证去重Map
    this._healthCheckPromises = new Map();
  }

  /**
   * 生成错误唯一标识
   */
  generateErrorId(error) {
    const keyFields = {
      runtime: [error.message, error.lineno, error.colno, error.filename],
      resource: [error.tagName, error.resourceUrl],
      promise: [error.message],
      vue: [error.message, error.component],
      manual: [error.message, JSON.stringify(error.extra || {})],
    };
    const fields = keyFields[error.type] || [];
    fields.push(this.options.appId);
    fields.push(this.options.extraData.version);
    fields.push(this.options.extraData.env);
    let key = fields.filter(Boolean).toString().replace(/\s/g, "");
    return MD5(key);
  }

  /**
   * 初始化错误监控
   */
  init() {
    if (this.initialized) return;
    this.initialized = true;
    // 捕获JavaScript运行时错误
    window.addEventListener("error", this.handleRuntimeError.bind(this), true);

    // 捕获资源加载错误
    window.addEventListener("error", this.handleResourceError.bind(this), true);

    // 捕获未处理的Promise rejection
    window.addEventListener("unhandledrejection", this.handlePromiseError.bind(this));

    // 初始化breadcrumbs监听器
    this.initBreadcrumbs();

    // 初始化上报定时器
    this.initReportTimer();

    this.debugLog("Error tracker initialized");
  }

  /**
   * 添加错误到队列（防止重复上报）
   */
  addErrorToQueue(error) {
    const errorId = this.generateErrorId(error);

    // 检查是否已存在相同错误标识
    const exists = this.errorQueue.some((e) => this.generateErrorId(e) === errorId);
    if (exists) {
      this.debugLog("Duplicate error skipped:", error);
      return;
    }
    if (this.errorId.indexOf(errorId) > -1) return;
    // 控制队列最大长度
    if (this.errorQueue.length >= this.options.maxErrors) {
      this.errorQueue.shift();
    }
    this.errorId.push(errorId);
    error["errorId"] = errorId;
    (error["url"] = window.location.href),
      this.errorQueue.push({
        error,
        breadcrumbs: this.breadcrumbs,
      });
    // 清空已上报的breadcrumbs
    this.breadcrumbs = [];

    if (this.options.consoleError) {
      console.error("[ErrorTracker]", error);
    }

    if (this.options.immediate) {
      this.reportErrors();
    }
  }

  /**
   * 初始化breadcrumbs监听器
   */
  initBreadcrumbs() {
    const tracker = this; // 保存当前实例，避免在回调中引用错误的 instance

    // 监听点击事件
    document.addEventListener(
      "click",
      (event) => {
        const target = event.target;
        // 过滤 el-loading-mask 等非用户交互点击
        if (target.className && target.className.indexOf && target.className.indexOf("el-loading-mask") !== -1) {
          return;
        }

        // 记录breadcrumb
        tracker.addBreadcrumb({
          type: "ui.click",
          message: `Clicked on ${target.tagName.toLowerCase()}`,
          data: {
            target: target.tagName,
            id: target.id,
            className: target.className,
          },
        });
      },
      true
    );

    // 监听路由变化（如果使用history API）
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function () {
      const result = originalPushState.apply(this, arguments);
      tracker.addBreadcrumb({
        type: "navigation",
        message: `Pushed state to ${arguments[2]}`,
        data: {
          url: arguments[2],
        },
      });
      return result;
    };

    history.replaceState = function () {
      const result = originalReplaceState.apply(this, arguments);
      tracker.addBreadcrumb({
        type: "navigation",
        message: `Replaced state with ${arguments[2]}`,
        data: {
          url: arguments[2],
        },
      });
      return result;
    };

    // 监听hash变化
    window.addEventListener("hashchange", (event) => {
      tracker.addBreadcrumb({
        type: "navigation",
        message: `Hash changed from ${event.oldURL} to ${event.newURL}`,
        data: {
          oldURL: event.oldURL,
          newURL: event.newURL,
        },
      });
    });

    // 监听页面加载完成
    window.addEventListener("load", () => {
      tracker.addBreadcrumb({
        type: "navigation",
        message: "Page loaded",
        data: {
          url: window.location.href,
        },
      });
    });

    // 监听AJAX请求
    if (window.XMLHttpRequest) {
      const originalOpen = XMLHttpRequest.prototype.open;
      const originalSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function () {
        this._requestMethod = arguments[0];
        this._requestURL = arguments[1];
        return originalOpen.apply(this, arguments);
      };

      XMLHttpRequest.prototype.send = function () {
        const xhr = this;
        const startTime = Date.now();
        const requestData = arguments[0];

        const originalOnReadyStateChange = xhr.onreadystatechange;
        xhr.onreadystatechange = function () {
          if (xhr.readyState === 4) {
            const duration = Date.now() - startTime;
            tracker.addBreadcrumb({
              type: "http",
              message: `${xhr._requestMethod} ${xhr._requestURL}`,
              data: {
                method: xhr._requestMethod,
                url: xhr._requestURL,
                status: xhr.status,
                duration: duration,
                requestData: requestData || {},
                responseData: xhr.responseText || {},
              },
            });
          }

          if (originalOnReadyStateChange) {
            originalOnReadyStateChange.apply(this, arguments);
          }
        };

        return originalSend.apply(this, arguments);
      };
    }
  }

  /**
   * 添加breadcrumb到队列
   */
  addBreadcrumb(breadcrumb) {
    if (this.breadcrumbs.length >= this.options.maxBreadcrumbs) {
      this.breadcrumbs.shift();
    }

    const breadcrumbWithTimestamp = {
      ...breadcrumb,
      timestamp: new Date().getTime(),
    };

    this.breadcrumbs.push(breadcrumbWithTimestamp);
    this.debugLog("Breadcrumb added:", breadcrumbWithTimestamp);
  }

  /**
   * 处理运行时错误
   */
  handleRuntimeError(event) {
    // 过滤掉资源加载错误
    if (event.target && (event.target.src || event.target.href)) {
      return;
    }

    const error = {
      type: "runtime",
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error ? this.serializeErrorStack(event.error) : "",
    };
    if (!error.stack) return;
    this.addErrorToQueue(error);
  }

  /**
   * 资源健康验证（支持图片、样式表、脚本等）
   * @param {string} url 资源地址
   * @param {string} tagName 触发错误的元素标签名（IMG, LINK, SCRIPT 等）
   * @returns {Promise<boolean>} true: 资源可访问，false: 资源不可访问
   */
  checkResourceHealth(url, tagName) {
    // 图片类型：使用 Image 对象（贴近真实加载行为，且能利用缓存）
    if (tagName === 'IMG') {
      return new Promise((resolve) => {
        const img = new Image();
        let resolved = false;
        const timeout = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            resolve(false);
          }
        }, 5000);

        img.onload = () => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            resolve(true);
          }
        };
        img.onerror = () => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            resolve(false);
          }
        };
        img.src = url;
        if (img.complete) {
          clearTimeout(timeout);
          resolve(img.naturalWidth !== 0);
        }
      });
    }

    // 其他资源（CSS、JS、字体等）：使用 fetch HEAD 请求验证
    return this.fetchResourceHealth(url);
  }

  /**
   * 通过 fetch 验证资源健康（HEAD 请求）
   * @param {string} url
   * @returns {Promise<boolean>}
   */
  fetchResourceHealth(url) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    return fetch(url, {
      method: 'HEAD',
      signal: controller.signal,
      // 不携带凭证，避免跨域 cookie 问题，且不影响验证结果
      credentials: 'omit',
    })
      .then(response => {
        clearTimeout(timeoutId);
        // 只要状态码是 2xx 或 304（重定向后有效）即认为资源可访问
        return response.ok;
      })
      .catch(() => {
        clearTimeout(timeoutId);
        return false;
      });
  }

  /**
   * 对资源错误进行二次验证，确认其真实不可用后再上报
   * @param {HTMLElement} target 触发错误的目标元素
   * @param {object} error 已构造的错误对象
   */
  verifyAndReportResourceError(target, error) {
    const url = error.resourceUrl;
    if (!url) return;

    const tagName = target.tagName; // 例如 'IMG', 'LINK', 'SCRIPT'
    const cacheKey = `${tagName}|${url}`;

    if (this._healthCheckPromises.has(cacheKey)) {
      this.debugLog(`Health check already in progress for ${cacheKey}, waiting...`);
      this._healthCheckPromises.get(cacheKey).then((isHealthy) => {
        if (!isHealthy) {
          this.debugLog(`Resource ${url} confirmed unhealthy, reporting error.`);
          this.addErrorToQueue(error);
        } else {
          this.debugLog(`Resource ${url} is actually healthy, skip reporting.`);
        }
      });
      return;
    }

    const healthPromise = this.checkResourceHealth(url, tagName);
    this._healthCheckPromises.set(cacheKey, healthPromise);

    healthPromise
      .then((isHealthy) => {
        if (!isHealthy) {
          this.debugLog(`Resource ${url} confirmed unhealthy, reporting error.`);
          this.addErrorToQueue(error);
        } else {
          this.debugLog(`Resource ${url} is actually healthy, skip reporting.`);
        }
      })
      .catch((err) => {
        this.debugLog(`Health check error for ${url}:`, err);
        this.addErrorToQueue(error);
      })
      .finally(() => {
        setTimeout(() => {
          if (this._healthCheckPromises.get(cacheKey) === healthPromise) {
            this._healthCheckPromises.delete(cacheKey);
          }
        }, 10000);
      });
  }
  /**
   * 处理资源加载错误（带健康验证）
   */
  handleResourceError(event) {
    const target = event.target;
    let current_url = window.location.origin + window.location.pathname;
    let target_url = target.src || target.href;
    if (!target || !target_url || target_url == current_url || target_url == window.location.origin + "/") {
      return;
    }
    const error = {
      type: "resource",
      tagName: target.tagName,
      className: target.className,
      resourceUrl: target_url,
    }; 
    // 原逻辑是直接上报，现改为二次验证后再上报
    this.verifyAndReportResourceError(target, error);

  }

  /**
   * 处理Promise错误
   */
  handlePromiseError(event) {
    let reason = event.reason;
    let message = "Unhandled Promise rejection";
    let stack = "";
    if (typeof reason === "object" && ["NavigationDuplicated"].indexOf(event.reason.name) > -1) return;

    if (reason instanceof Error) {
      message = reason.message;
      stack = this.serializeErrorStack(reason);
    } else if (typeof reason === "string") {
      message = reason;
    } else if (reason) {
      try {
        message = reason;
      } catch (e) {
        console.error(e);
        message = String(reason);
      }
    }
    // 判断重定向
    const patterns = [
      "Avoided redundant navigation to current location",
      "Navigation cancelled from",
      "Unhandled Promise rejection",
      "Redirected when going from",
      "cancel"
    ];

    if (typeof message == 'string' && _.some(patterns, pattern => message.includes(pattern)) || !stack) return;

    if (message && (message.status == 200 || !message.status)) {
      const error = {
        type: "promise",
        message: message,
        stack: stack,
      };
      this.addErrorToQueue(error);
      return;
    }
  }

  /**
   * 序列化错误堆栈
   */
  serializeErrorStack(error) {
    if (!error || !error.stack) return "";

    try {
      // IE11的Error对象可能没有stack属性
      return error.stack || error.message || String(error);
    } catch (e) {
      console.error(e);
      return String(error);
    }
  }

  /**
   * 初始化上报定时器
   */
  initReportTimer() {
    if (this.reportTimer) {
      clearInterval(this.reportTimer);
    }

    this.reportTimer = setInterval(() => {
      if (this.errorQueue.length > 0) {
        this.reportErrors();
      }
    }, this.options.reportInterval);
  }

  /**
   * 上报错误
   */
  reportErrors() {
    if (!this.options.reportUrl || this.errorQueue.length === 0) {
      return;
    }

    const errorsToReport = [...this.errorQueue];
    this.errorQueue = [];

    const reportData = {
      appId: this.options.appId,
      timestamp: new Date().getTime(),
      userAgent: navigator.userAgent,
      errors: errorsToReport,
      ...this.options.extraData,
    };

    this.debugLog("Reporting errors:", reportData);

    // 使用navigator.sendBeacon或XMLHttpRequest上报
    if (typeof navigator.sendBeacon === "function") {
      try {
        const blob = new Blob([JSON.stringify(reportData)], { type: "application/json" });
        navigator.sendBeacon(this.options.reportUrl, blob);
      } catch (e) {
        console.error(e);
        this.sendByXHR(reportData);
      }
    } else {
      this.sendByXHR(reportData);
    }
  }

  /**
   * 使用XMLHttpRequest上报
   */
  sendByXHR(data) {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", this.options.reportUrl, true);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.send(JSON.stringify(data));
    } catch (e) {
      console.log("[ErrorTracker] Failed to report errors:", e);
    }
  }

  /**
   * 手动捕获错误
   */
  captureError(error, extraInfo = {}) {
    let errorData = {
      type: "manual",
    };

    if (error instanceof Error) {
      errorData.message = error.message;
      errorData.stack = this.serializeErrorStack(error);
    } else if (typeof error === "string") {
      errorData.message = error;
    } else {
      try {
        errorData.message = JSON.stringify(error);
      } catch (e) {
        console.error(e);
        errorData.message = String(error);
      }
    }

    if (extraInfo) {
      errorData.extra = extraInfo;
    }

    this.addErrorToQueue(errorData);
  }

  /**
   * 调试日志
   */
  debugLog(...args) {
    if (this.options.debug) {
      console.log("[ErrorTracker Debug]", ...args);
    }
  }

  /**
   * 销毁
   */
  destroy() {
    window.removeEventListener("error", this.handleRuntimeError);
    window.removeEventListener("error", this.handleResourceError);
    window.removeEventListener("unhandledrejection", this.handlePromiseError);

    if (this.reportTimer) {
      clearInterval(this.reportTimer);
      this.reportTimer = null;
    }

    this.initialized = false;
    this.debugLog("Error tracker destroyed");
  }
}

// 单例模式
let instance = null;

function initErrorTracker(options) {
  if (!instance) {
    instance = new ErrorTracker(options);
    instance.init();
  }
  return instance;
}

const VueErrorPlugin = {
  install(Vue, options) {
    // 初始化错误跟踪器
    const tracker = initErrorTracker(options);

    // 添加Vue错误处理器
    Vue.config.errorHandler = (err, vm, info) => {
      // 过滤 vue 路由重定向错误和路由取消错误
      if (err.name === "NavigationDuplicated" || err.message.includes("Avoided redundant navigation") || err.name === "NavigationCancelled" || err.message.includes("Navigation cancelled")) {
        tracker.debugLog("Ignored vue router error:", err.message);
        return;
      }

      const error = {
        type: "vue",
        message: err.message,
        stack: tracker.serializeErrorStack(err),
        component: vm ? vm.$options.name : "unknown",
        info: info,
      };
      tracker.addErrorToQueue(error);

      if (process.env.NODE_ENV === "development") {
        console.error(err);
      }
    };

    // 添加Vue路由变化的breadcrumbs（可拓展）
    Vue.mixin({
      beforeCreate() {
        // 预留扩展点
      },
    });
  },
};

export default VueErrorPlugin;